import { AutoComplete as AntAutoComplete, AutoCompleteProps } from "antd";
import { useLayoutEffect, useRef, useState } from "react";
import { twMerge } from "tailwind-merge";
import { InputLayout } from "../layouts";

interface Option {
  value: string;
  label: string;
  desc?: string;
  icon?: React.ReactNode;
}

interface Props extends Omit<AutoCompleteProps, "options"> {
  label?: string;
  rules?: { required?: boolean }[];
  options?: Option[];
  addonAfter?: React.ReactNode;
}

const AutoComplete = ({
  label,
  rules,
  placeholder,
  options,
  addonAfter,
  className,
  style,
  onFocus,
  onSearch,
  ...props
}: Props) => {
  const hostRef = useRef<HTMLDivElement>(null);
  const [filtering, setFiltering] = useState(false);
  const [popup, setPopup] = useState({ width: 0, x: 0, below: 4, above: 4 });

  useLayoutEffect(() => {
    const host = hostRef.current;
    const field = host?.closest(".group") as HTMLElement | null;
    const select = host?.querySelector(".ant-select") as HTMLElement | null;
    if (!field || !select) return;
    const measure = () => {
      const fieldBox = field.getBoundingClientRect();
      const selectBox = select.getBoundingClientRect();
      const gap = 4;
      setPopup({
        width: Math.round(fieldBox.width),
        x: Math.round(fieldBox.left - selectBox.left),
        below: Math.round(fieldBox.bottom - selectBox.bottom + gap),
        above: Math.round(selectBox.top - fieldBox.top + gap),
      });
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(field);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  return (
    <InputLayout
      label={label}
      rules={rules}
      style={style}
      className={twMerge(
        "[&.app-field_.ant-select-auto-complete]:!h-[2.4rem] [&.app-field_.ant-select-auto-complete]:!min-h-[2.4rem]",
        "[&.app-field_.ant-select-auto-complete_.ant-select-selector]:!h-[2.4rem] [&.app-field_.ant-select-auto-complete_.ant-select-selector]:!min-h-[2.4rem] [&.app-field_.ant-select-auto-complete_.ant-select-selector]:!p-0",
        "[&_.ant-select-selection-search-input]:!h-[2.4rem] [&_.ant-select-selection-search-input]:!text-[1.6rem] [&_.ant-select-selection-search-input]:!leading-[2.4rem]",
        className,
      )}
    >
      <div
        ref={hostRef}
        className="flex w-full min-w-0 items-center gap-[1.2rem]"
      >
        <AntAutoComplete
          {...props}
          className="min-w-0 flex-1"
          options={options}
          placeholder={placeholder}
          virtual={false}
          popupMatchSelectWidth={popup.width || true}
          builtinPlacements={{
            bottomLeft: {
              points: ["tl", "bl"],
              offset: [popup.x, popup.below],
              overflow: { adjustX: 0, adjustY: 1 },
              dynamicInset: true,
            },
            bottomRight: {
              points: ["tr", "br"],
              offset: [-popup.x, popup.below],
              overflow: { adjustX: 0, adjustY: 1 },
              dynamicInset: true,
            },
            topLeft: {
              points: ["bl", "tl"],
              offset: [popup.x, -popup.above],
              overflow: { adjustX: 0, adjustY: 1 },
              dynamicInset: true,
            },
            topRight: {
              points: ["br", "tr"],
              offset: [-popup.x, -popup.above],
              overflow: { adjustX: 0, adjustY: 1 },
              dynamicInset: true,
            },
          }}
          getPopupContainer={() => document.body}
          dropdownStyle={{ zIndex: 2200 }}
          popupClassName="[&_.ant-select-item]:!min-h-[5.5rem] [&_.ant-select-item]:!items-center"
          onFocus={(event) => {
            setFiltering(false);
            onFocus?.(event);
          }}
          onSearch={(text) => {
            if (text !== props.value) setFiltering(true);
            onSearch?.(text);
          }}
          optionRender={(option) => (
            <span className="flex w-full items-center gap-[1rem]">
              {option.data.icon}
              <span className="min-w-0 flex-1">
                <span className="block text-[1.5rem] leading-[2rem]">
                  {option.data.label}
                </span>
                {!!option.data.desc && (
                  <span className="block text-[1.3rem] leading-[1.8rem] text-black/45">
                    {option.data.desc}
                  </span>
                )}
              </span>
            </span>
          )}
          filterOption={
            filtering
              ? (input, option) =>
                  `${option?.label ?? ""} ${option?.desc ?? ""}`
                    .toLowerCase()
                    .includes(String(input).toLowerCase())
              : () => true
          }
        />
        {addonAfter}
      </div>
    </InputLayout>
  );
};

export default AutoComplete;
